import React, {FC, useEffect, useMemo} from 'react';
import styled from 'styled-components';

import {Plural} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {makeGetSimilarInquiriesCount} from '../../../../../store/data/similarInquiryLists/similarInquiryListsSelectors';
import {fetchSimilarInquiryListsAsyncAction} from '../../../../../store/data/similarInquiryLists/similarInquiryListsThunkActions';
import {toggleSynopsisAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {SynopsisFiltersEnum} from '../../../../../store/panels/viewer/viewerPanelState';

interface ConversationSimilarInquiriesButtonProps {
  className?: string;
  conversationId: number;
  panelId: ViewerPanelId;
}

const StyledButton = styled(ChromelessButton)`
  background-color: ${(p) => p.theme.palette.grey.shade10};
  border-radius: 100px;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 1.5px 8px;
  height: 20px;
  font-size: ${fontSizes.verySmall};

  margin-left: 1px;
  margin-right: 8px;
  color: ${(p) => p.theme.palette.grey.shade40};
  border: ${(p) => p.theme.palette.grey.shade40} 1px solid;
  font-weight: ${fontWeights.semibold};

  &:hover {
    background-color: ${(p) => p.theme.palette.grey.shade20};
    border-color: ${(p) => p.theme.palette.grey.shade50};
  }
`;

export const ConversationSimilarInquiriesButton: FC<ConversationSimilarInquiriesButtonProps> = ({
  className,
  conversationId,
  panelId,
}) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();
  const getSimilarInquiriesCount = useMemo(
    () => makeGetSimilarInquiriesCount(conversationId),
    [conversationId],
  );
  const numSimilarInquiries = useAppSelector(getSimilarInquiriesCount);

  useEffect(() => {
    dispatch(fetchSimilarInquiryListsAsyncAction(conversationId));
  }, [conversationId, dispatch]);

  if (numSimilarInquiries === 0) {
    return null;
  }

  return (
    <StyledButton
      className={className}
      onClick={() => dispatch(toggleSynopsisAction(panelId, true, SynopsisFiltersEnum.SIMILAR_INQUIRIES))}
    >
      <Icon name="sparklesAiFilledTiny" color={theme.palette.grey.shade40} />
      <Plural id="similar-inquiries-button" one="1 similar" other="# similar" value={numSimilarInquiries} />
    </StyledButton>
  );
};
